/**
 * Utilidades de teléfono para los formularios de captación de leads (landings).
 *
 * Apoyado en `libphonenumber-js` (validación + metadata de países) y en
 * `Intl.DisplayNames` (nombres de país en español, sin dependencia extra).
 *
 * Fuente única de verdad de la validación: `isValidLandingPhone(value)` recibe
 * el valor en **E.164** (`+34600100200`) — que es lo que el `PhoneField` guarda
 * en el formulario — y delega en `isValidPhoneNumber` de libphonenumber-js, así
 * que acepta cualquier número válido del país (móvil o fijo).
 */
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

export interface CountryOption {
  /** ISO 3166-1 alpha-2, p. ej. "ES". */
  iso: CountryCode;
  /** Código de marcación sin "+", p. ej. "34". */
  dialCode: string;
  /** Nombre del país en español, p. ej. "España". */
  name: string;
  /** Emoji de bandera, p. ej. "🇪🇸". */
  flag: string;
}

export const DEFAULT_COUNTRY: CountryCode = "ES";

const regionNames = new Intl.DisplayNames(["es"], { type: "region" });

/** ISO2 → emoji de bandera (regional indicator symbols). */
export function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/** Países prioritarios (audiencia Migro) primero; el resto, alfabético. */
const PRIORITY: string[] = [
  "ES", "MA", "CO", "VE", "EC", "PE", "BO", "AR", "BR", "DO", "CU", "PY",
  "HN", "CL", "UY", "NI", "SV", "GT", "RO", "IT", "FR", "PT", "DE", "GB", "US",
];

/** Todos los países, con bandera + nombre + código de marcación. */
export const COUNTRY_OPTIONS: CountryOption[] = (() => {
  const rank = new Map(PRIORITY.map((iso, i) => [iso, i]));
  return getCountries()
    .map((iso) => ({
      iso,
      dialCode: getCountryCallingCode(iso),
      name: regionNames.of(iso) ?? iso,
      flag: flagEmoji(iso),
    }))
    .sort((a, b) => {
      const ra = rank.has(a.iso) ? (rank.get(a.iso) as number) : Infinity;
      const rb = rank.has(b.iso) ? (rank.get(b.iso) as number) : Infinity;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name, "es");
    });
})();

/** Devuelve la opción de país por ISO, con fallback a España. */
export function getCountryOption(iso: string): CountryOption {
  return (
    COUNTRY_OPTIONS.find((c) => c.iso === iso) ??
    (COUNTRY_OPTIONS.find((c) => c.iso === DEFAULT_COUNTRY) as CountryOption)
  );
}

/** Construye el E.164 a partir de país + número nacional (tal como se teclea). */
export function toE164(iso: CountryCode, national: string): string {
  const digits = (national || "").replace(/\D/g, "");
  if (!digits) return "";
  return `+${getCountryCallingCode(iso)}${digits}`;
}

/**
 * Deriva `{ iso, national }` de un valor almacenado (E.164 o crudo) para pintar
 * el control. Si no se reconoce el país, cae a España conservando los dígitos
 * (cubre leads históricos guardados como "600100200").
 */
export function parseE164(value: string | null | undefined): {
  iso: CountryCode;
  national: string;
} {
  if (!value) return { iso: DEFAULT_COUNTRY, national: "" };
  const parsed = parsePhoneNumberFromString(value);
  if (parsed?.country) {
    return { iso: parsed.country, national: parsed.nationalNumber };
  }
  return { iso: DEFAULT_COUNTRY, national: value.replace(/\D/g, "") };
}

/**
 * Validación de teléfono de landing. `value` viene en E.164 (lo que guarda el
 * `PhoneField`), así que el país se infiere del propio número.
 */
export function isValidLandingPhone(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}
